/*
LIME LABS COMMERCE LAYER
Component: PDP V6.5 Interaction Runtime
Project: La Troupe
Version: 6.5
Status: LLCL-QA

Interaction-only progressive enhancement.
- First-paint presentation authority lives in render-blocking LLCL CSS in HEAD.
- Adds reliable slow gallery auto-scroll using a fractional accumulator.
- Observes visibility changes only to refresh scroll geometry after variant-media updates.
- Never injects presentation CSS.
- Never filters, reorders, clones, unhides, or remaps media.
*/

(() => {
  'use strict';

  if (window.__LLCL_PDP_V64_LOADED_RUNTIME__) return;
  window.__LLCL_PDP_V64_LOADED_RUNTIME__ = true;

  const GALLERY_SELECTOR = '.shopify-section--main-product product-gallery.product-gallery';
  const RAIL_SELECTOR = '.product-gallery__carousel';
  const SPEED = 10;
  const INITIAL_DELAY = 1000;
  const INTERACTION_HOLD = 3800;
  const EDGE_HOLD = 700;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const boundRails = new WeakSet();

  const visibleCells = rail => Array.from(
    rail.querySelectorAll('.product-gallery__media:not([hidden])')
  );

  const bind = gallery => {
    if (!(gallery instanceof Element)) return;

    const rail = gallery.querySelector(RAIL_SELECTOR);
    if (!(rail instanceof HTMLElement) || boundRails.has(rail)) return;

    boundRails.add(rail);
    gallery.dataset.llPdpV64Autoscroll = 'true';

    let direction = 1;
    let pointerActive = false;
    let focused = false;
    let pauseUntil = performance.now() + INITIAL_DELAY;
    let lastTime = performance.now();
    let virtualLeft = Number(rail.scrollLeft) || 0;

    const maxScroll = () => Math.max(0, rail.scrollWidth - rail.clientWidth);

    const pauseFor = (duration = INTERACTION_HOLD) => {
      virtualLeft = Number(rail.scrollLeft) || 0;
      pauseUntil = Math.max(pauseUntil, performance.now() + duration);
    };

    const refresh = () => {
      const max = maxScroll();
      virtualLeft = Math.min(max, Math.max(0, Number(rail.scrollLeft) || 0));
      if (virtualLeft <= .5) direction = 1;
      if (max > 1 && virtualLeft >= max - .5) direction = -1;
      lastTime = performance.now();
    };

    const refreshAfterLayout = () => requestAnimationFrame(() => requestAnimationFrame(refresh));

    rail.addEventListener('pointerdown', () => {
      pointerActive = true;
      pauseFor();
    }, { passive: true });

    const endPointer = () => {
      pointerActive = false;
      pauseFor();
    };

    rail.addEventListener('pointerup', endPointer, { passive: true });
    rail.addEventListener('pointercancel', endPointer, { passive: true });
    rail.addEventListener('wheel', () => pauseFor(), { passive: true });
    rail.addEventListener('touchstart', () => pauseFor(), { passive: true });
    rail.addEventListener('keydown', () => pauseFor());
    rail.addEventListener('click', () => pauseFor(), true);

    gallery.addEventListener('focusin', () => {
      focused = true;
      pauseFor();
    });

    gallery.addEventListener('focusout', event => {
      if (!gallery.contains(event.relatedTarget)) {
        focused = false;
        pauseFor(700);
      }
    });

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(refreshAfterLayout);
      observer.observe(rail);
    }

    if ('MutationObserver' in window) {
      const observer = new MutationObserver(records => {
        if (records.some(record => record.attributeName === 'hidden')) refreshAfterLayout();
      });
      observer.observe(rail, {
        subtree: true,
        attributes: true,
        attributeFilter: ['hidden']
      });
    }

    const tick = now => {
      if (!gallery.isConnected || !rail.isConnected) return;

      const elapsed = Math.min(50, Math.max(0, now - lastTime));
      lastTime = now;
      const max = maxScroll();
      const cells = visibleCells(rail);
      const paused = reducedMotion.matches || document.hidden || pointerActive || focused || now < pauseUntil;

      if (cells.length > 1 && max > 2 && !paused) {
        virtualLeft += direction * SPEED * (elapsed / 1000);

        if (virtualLeft >= max) {
          virtualLeft = max;
          direction = -1;
          pauseUntil = now + EDGE_HOLD;
        } else if (virtualLeft <= 0) {
          virtualLeft = 0;
          direction = 1;
          pauseUntil = now + EDGE_HOLD;
        }

        rail.scrollLeft = virtualLeft;
      }

      requestAnimationFrame(tick);
    };

    document.addEventListener('visibilitychange', refresh);
    reducedMotion.addEventListener?.('change', refresh);

    refresh();
    requestAnimationFrame(tick);
  };

  const boot = (scope = document) => {
    if (scope.matches?.(GALLERY_SELECTOR)) bind(scope);
    scope.querySelectorAll?.(GALLERY_SELECTOR).forEach(bind);
  };

  const delayedBoot = () => requestAnimationFrame(() => requestAnimationFrame(() => boot(document)));

  document.addEventListener('shopify:section:load', event => boot(event.target || document));
  document.addEventListener('product:rerender', delayedBoot);
  document.addEventListener('variant:change', delayedBoot);
  document.addEventListener('llcl:variant-media-state', delayedBoot);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  } else {
    boot(document);
  }
})();
